import { Component, Input, OnInit } from '@angular/core';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { ModuleManager } from 'src/shared/models/module-manager';
import { ModuleManagerSetting } from 'src/shared/models/module-manager/module-manager-setting.model';
import { DataFieldDefinition, ReportSelectorSettings, ReportingSetDefinition, ReportingSetDefinitionApiModel } from '../../../../../shared/models/pipeline-config/reporting-sets.models';
import { DateObject } from '../../modules/report/report.model';
import { ReportService } from '../../modules/report/report.service';
import { cloneDeep } from 'lodash';

@Component({
    selector: 'cv-report-settings',
    templateUrl: './report-settings.component.html',
    styleUrls: ['./report-settings.component.scss']
})
export class ReportSettingsComponent implements OnInit {
    @Input() moduleManager: ModuleManager;

    /** Fully loaded API Models list (including the Reporting Set Definition objects themselves and wrapper helper properties) */
    definitions: ReportingSetDefinitionApiModel[] = [];

    /** These are the options that are available in the Report dropdown list */
    reportOptions: ReportingSetDefinition[] = [];
    /** These are the options that are available in the Date Field dropdown list */
    dates: DateObject[] = [];
    /** These are the options that are available in the Group Rows By dropdown list */
    fieldDefinitions: DataFieldDefinition[] = [];

    selectedReportApiModel: ReportingSetDefinitionApiModel;
    selectedReport: ReportingSetDefinition;
    prevSelectedReport: ReportingSetDefinition;
    selectedReportName;
    selectedDateField: DateObject;
    selectedDateName;
    selectedGroupByRow: DataFieldDefinition;
    selectedGroupByRowName;
    refreshFilters: boolean = false;

    private requireDate: boolean = false;

    constructor(
        private localizationService: CVLocalizationService,
        private service: ReportService,
    ) { }

    get stateKey() {
        return this.moduleManager?.getModuleKey();
    }

    ngOnInit(): void {
        this.moduleManager.registerEvents({
            onSettingsOpened: this.onSettingsOpened.bind(this),
            onSettingsCancelled: this.onSettingsCancelled.bind(this),
        });

        this.moduleManager.addSetting({
            name: 'reportingSetName',
            required: true,
            getValue: () => this.selectedReportName,
            onSettingLoaded: (value) => {
                if (!value)
                    return;

                this.selectedReportName = value;
                if (this.definitions !== null) {
                    this.selectedReportApiModel = this.definitions.find(rs => rs.ReportingSetDefinition?.ReportingSetName === value);
                    this.selectedReport = this.selectedReportApiModel?.ReportingSetDefinition;
                    this.onReportSelected();
                }
            },
            defaultValue: undefined,
        } as ModuleManagerSetting<string>);

        /**
         * dateField is not always a required field.
         */
        this.moduleManager.addSetting({
            name: 'dateField',
            required: this.requireDate,
            getValue: () => this.selectedDateName,
            onSettingLoaded: (value) => {
                if (!value)
                    return;

                this.selectedDateName = value;
                if (this.dates)
                    this.selectedDateField = this.dates.find(d => d.name === value);
            },
            defaultValue: undefined,
        } as ModuleManagerSetting<string>);

        this.moduleManager.addSetting({
            name: 'groupByRow',
            required: true,
            getValue: () => this.selectedGroupByRowName,
            onSettingLoaded: (value) => {
                if (!value)
                    return;

                this.selectedGroupByRowName = value;
                if (this.fieldDefinitions !== null) {
                    this.selectedGroupByRow = this.fieldDefinitions.find(fd => fd.ColumnField === value || fd.ColumnField.replace("DataField_", "") === value);
                }
            },
            defaultValue: "None",
        } as ModuleManagerSetting<string>);

        this.loadData();
    }

    async loadData() {
        const tasks: [Promise<ReportingSetDefinitionApiModel[]>, Promise<Map<string, string>>] = [
            this.service.loadReportDefinitions(this.moduleManager.container.moduleId),
            this.localizationService.GetSection('Report_Widget')
        ];
        const results = await Promise.all(tasks);

        this.definitions = [];
        if (results[0].length > 0) {
            results[0].forEach(r => {
                const localizedReport = this.localizationService.getCachedText('Report_Widget', r.ReportingSetDefinition.ReportingSetName);
                r.ReportingSetDefinition.DisplayName = localizedReport;
                this.definitions.push(r);
            });

            // If there is a report selected
            if (this.selectedReportName) {
                this.selectedReportApiModel = this.definitions.find(rs => rs.ReportingSetDefinition.ReportingSetName === this.selectedReportName);
                this.selectedReport = this.selectedReportApiModel?.ReportingSetDefinition;
            }

            this.reportOptions = this.definitions.map(d => d.ReportingSetDefinition);

            this.onReportSelected();
        }
    }

    async loadFieldDefinitions() {
        this.fieldDefinitions = [];
        if (this.selectedReportApiModel?.SupportsGrouping) {
            // Creating field definitions (Group Rows By dropdown options).
            const dataFields = Object.values(this.selectedReportApiModel.ReportingSetDefinition.DataFields).filter(df => df['Groupable'] === true);
            dataFields.forEach(df => {
                df.ColumnField = "DataField_" + df.Field;
            })

            const attributesEntries = Object.entries(this.selectedReportApiModel.ReportingSetDefinition.Attributes);
            attributesEntries.forEach(a => {
                a[1].ColumnField = "Attribute_" + a[0];
            })
            const attributes = attributesEntries.map(ae => ae[1]).filter(af => af['Groupable'] === true);

            const datesEntries = Object.entries(this.selectedReportApiModel.ReportingSetDefinition.Dates);
            datesEntries.forEach(d => {
                d[1].ColumnField = "Date_" + d[0];
            })
            const dates = datesEntries.map(de => de[1]).filter(df => df['Groupable'] === true);

            const contactHierarchyEntries = Object.entries(this.selectedReportApiModel.ReportingSetDefinition.ContactHierarchy);
            contactHierarchyEntries.forEach(ch => {
                ch[1].ColumnField = "ContactHierarchy_" + ch[0];
            })
            const contactHierarchy = contactHierarchyEntries.map(che => che[1]).filter(ch => ch['Groupable'] === true);

            const empHierarchyEntries = Object.entries(this.selectedReportApiModel.ReportingSetDefinition.EmpHierarchy);
            empHierarchyEntries.forEach(eh => {
                eh[1].ColumnField = "EmpHierarchy_" + eh[0];
            })
            const empHierarchy = empHierarchyEntries.map(ehe => ehe[1]).filter(ch => ch['Groupable'] === true);

            this.fieldDefinitions = this.fieldDefinitions.concat(dataFields, attributes, dates, contactHierarchy, empHierarchy);

            this.fieldDefinitions.push({
                Field: "None",
                ColumnField: "None",
                Groupable: true,
                DisplayName: this.localizationService.getCachedText("Report_Widget", "None", "None"),
            } as any);

            this.fieldDefinitions.forEach((fd: DataFieldDefinition) => {
                fd.DisplayName = fd.DisplayName.toUpperCase();
            });

            if (this.selectedGroupByRowName != undefined) {
                this.selectedGroupByRow = this.fieldDefinitions.find(fd => fd.ColumnField === this.selectedGroupByRowName || fd.ColumnField.replace("DataField_", "") === this.selectedGroupByRowName);
            } else {
                this.selectedGroupByRowName = "None";
                this.selectedGroupByRow = this.fieldDefinitions.find(fd => fd.ColumnField === this.selectedGroupByRowName);
            }
        } else {
            this.selectedGroupByRowName = "None";
            this.selectedGroupByRow = null;
        }
    }

    async onSettingsOpened() {        
        await this.loadFieldDefinitions();
        this.changeGroupFilters();
        this.prevSelectedReport = { ...this.selectedReport };
    }

    onSettingsCancelled() {
        this.selectedReportName = this.prevSelectedReport?.ReportingSetName;
        this.selectedReportApiModel = this.definitions.find(rs => rs.ReportingSetDefinition.ReportingSetName === this.selectedReportName);
        this.selectedReport = this.selectedReportApiModel?.ReportingSetDefinition;
        this.onReportSelected();
        this.changeGroupFilters();
    }

    getLocalizedText(key: string, defaultValue: string = "") {
        return this.localizationService.getCachedText('Report_Widget', key, defaultValue);
    }

    onReportSelected() {
        this.selectedReportApiModel = this.definitions.find(d => d.ReportingSetNameForReports === this.selectedReport?.ReportingSetName);
        this.refreshFilters = true;
        this.selectedReportName = this.selectedReport?.ReportingSetName;

        this.dates = [];
        const loadedDates = this.selectedReport ? Object.keys(this.selectedReport.Dates) : [];

        this.dates = loadedDates.map(d => {
            let displayName = this.localizationService.getCachedText('Report_Widget', d, "");
            if (displayName === "") {
                displayName = this.selectedReport.Dates[d].DisplayName;
            }
            return {
                name: d,
                displayName,
            };
        });

        this.dates.sort((a, b) => a.displayName.localeCompare(b.displayName));

        if (this.dates?.length > 0) {
            this.selectedDateField = this.dates[0];
            this.selectedDateName = this.selectedDateField.name;
            this.requireDate = true;
        } else {
            this.selectedDateField = undefined;
            this.selectedDateName = null;
            this.dates = null;
            this.requireDate = false;
        }
        this.moduleManager.container.moduleInfo.DTO.SettingDateRangeSelector = this.requireDate;
        this.moduleManager.container.toolbarHidden = !this.requireDate;
        this.moduleManager.customSettings['dateField'].required = this.requireDate;

        this.changeGroupFilters();

        const container = this.moduleManager.container;
        if (container.settingsOpen || (!container.moduleManager.loadedStateValues.maxDateRange && !container.settingsLoading && !container.genericSettingsLoading)) {
            this.selectedGroupByRowName = "None";
            this.loadFieldDefinitions();
            this.changeDateRangeOptions();
        }

        this.refreshFilters = false;
    }

    async changeGroupFilters() {
        if (this.selectedReportName === undefined && (!this.dates && this.requireDate) && this.selectedDateName == undefined) {
            const reportSettings: ReportSelectorSettings = {
                HasEmpSelector: false,
                EmpSelectorLevels: [],
                HasRecSelector: false,
                RecSelectorLevels: [],
                HasDateRangeSelector: false,
                ExtraElements: []
            }
            this.moduleManager.updateWidgetSettings(reportSettings);
        } else if (!!this.selectedReport?.ReportSelectorSettings) {
            if (this.requireDate) {
                this.selectedReport.ReportSelectorSettings.HasDateRangeSelector = true;
            } else {
                this.selectedReport.ReportSelectorSettings.HasDateRangeSelector = false;
            }
            this.moduleManager.updateWidgetSettings(this.selectedReport.ReportSelectorSettings);
        }
    }

    onReportChanged(change: string) {
        this.selectedReportApiModel = this.definitions.find(d => d.ReportingSetDefinition._id === change);
        this.selectedReport = this.selectedReportApiModel.ReportingSetDefinition;
        this.onReportSelected();
    }

    onDateSelected() {
        this.selectedDateName = this.selectedDateField.name;
    }

    onGroupByRowSelected() {
        this.selectedGroupByRowName = this.selectedGroupByRow.ColumnField;
    }

    async changeDateRangeOptions() {
        let report = this.selectedReport;

        this.moduleManager.moduleSettings.reportingSetName = report?.ReportingSetName ?? undefined;
        this.moduleManager.setMaxDateRange(report?.MaxReportDays && Number(report?.MaxReportDays) > 0 ? report?.MaxReportDays : 14);
        this.moduleManager.loadedStateValues.maxDateRange = this.moduleManager.maxDateRange;
        this.moduleManager.container.moduleManager.loadedStateValues.maxDateRange = this.moduleManager.maxDateRange;

        this.moduleManager.container.genericSettingsData = null;
        await this.moduleManager.loadGenericSettings();
    }

    public getSelectedReportFilterableFieldsCopy() {
        return cloneDeep(this.selectedReport.ReportFilterableFields);
    }
}
